import { formatU, formatFecha } from './ui.tsx';

export interface ColumnaTabla {
  clave: string;
  etiqueta: string;
  formato?: 'moneda' | 'fecha' | 'numero' | 'texto';
}

function formatearCelda(valor: unknown, formato?: string): string {
  if (valor === null || valor === undefined) return '—';
  switch (formato) {
    case 'moneda':
      return formatU(Number(valor));
    case 'fecha':
      return formatFecha(String(valor));
    case 'numero':
      return typeof valor === 'number' ? valor.toLocaleString('es-UY') : String(valor);
    default:
      return String(valor);
  }
}

function descargar(blob: Blob, nombre: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Genera y descarga un CSV (sin dependencias externas). */
export function exportarCSV(
  datos: Record<string, unknown>[],
  columnas: ColumnaTabla[],
  nombreArchivo: string,
): void {
  const encabezado = columnas.map((c) => `"${c.etiqueta}"`).join(',');
  const filas = datos.map((fila) =>
    columnas
      .map((c) => {
        const val = fila[c.clave];
        const texto = formatearCelda(val, c.formato);
        return `"${texto.replace(/"/g, '""')}"`;
      })
      .join(','),
  );
  const csv = '\uFEFF' + encabezado + '\n' + filas.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  descargar(blob, nombreArchivo);
}

/** Genera y descarga un PDF con tabla (carga jspdf bajo demanda). */
export async function exportarPDF(
  datos: Record<string, unknown>[],
  columnas: ColumnaTabla[],
  titulo: string,
  nombreArchivo: string,
): Promise<void> {
  const [{ jsPDF }, autoTableMod] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  const autoTable = autoTableMod.default;

  const doc = new jsPDF({ orientation: columnas.length > 5 ? 'landscape' : 'portrait' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(titulo, doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

  const head = [columnas.map((c) => c.etiqueta)];
  const body = datos.map((fila) =>
    columnas.map((c) => formatearCelda(fila[c.clave], c.formato)),
  );

  autoTable(doc, {
    head,
    body,
    startY: 22,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [63, 86, 140], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  });

  doc.save(nombreArchivo);
}
